import { AFFIXES, COMPONENTS } from "../content/affixes";
import { applyPathModifiers } from "../content/skill-paths";
import { skillNodeById } from "../content/skill-tree";
import type { RunInventory } from "./snapshot";

export type Modifiers = {
  damageMult: number;
  shieldMult: number;
  speedMult: number;
  salvageMult: number;
  cooldownMult: number;
  hullBonus: number;
  shieldBonus: number;
  boostBonus: number;
  extractBonus: number;
  armorBonus: number;
  armorAbsorbPct: number;
  shieldRegenDelayBonus: number;
  pickupRadiusMult: number;
  eliteScoreMult: number;
  mineTelegraphEarlyS: number;
  showThreatIntel: boolean;
};

export function createBaseModifiers(): Modifiers {
  return {
    damageMult: 1,
    shieldMult: 1,
    speedMult: 1,
    salvageMult: 1,
    cooldownMult: 1,
    hullBonus: 0,
    shieldBonus: 0,
    boostBonus: 0,
    extractBonus: 0,
    armorBonus: 0,
    armorAbsorbPct: 0,
    shieldRegenDelayBonus: 0,
    pickupRadiusMult: 1,
    eliteScoreMult: 1,
    mineTelegraphEarlyS: 0,
    showThreatIntel: false,
  };
}

function applySkillEffect(mods: Modifiers, nodeId: string): void {
  const node = skillNodeById(nodeId);
  if (!node) return;
  const e = node.effects;
  mods.damageMult *= e.damageMult ?? 1;
  mods.shieldMult *= e.shieldMult ?? 1;
  mods.speedMult *= e.speedMult ?? 1;
  mods.salvageMult *= e.salvageMult ?? 1;
  mods.cooldownMult *= e.cooldownMult ?? 1;
  mods.hullBonus += e.hullBonus ?? 0;
  mods.shieldBonus += e.shieldBonus ?? 0;
  mods.boostBonus += e.boostBonus ?? 0;
  mods.extractBonus += e.extractBonus ?? 0;
  mods.armorBonus += e.armorBonus ?? 0;
  mods.armorAbsorbPct = Math.max(mods.armorAbsorbPct, e.armorAbsorbPct ?? 0);
  mods.shieldRegenDelayBonus += e.shieldRegenDelayBonus ?? 0;
  mods.pickupRadiusMult *= e.pickupRadiusMult ?? 1;
  mods.eliteScoreMult *= e.eliteScoreMult ?? 1;
  mods.mineTelegraphEarlyS = Math.max(mods.mineTelegraphEarlyS, e.mineTelegraphEarlyS ?? 0);
  if (e.showThreatIntel) mods.showThreatIntel = true;
}

export function computeModifiers(
  inventory: RunInventory,
  takenNodes: readonly string[] = [],
): Modifiers {
  const mods = createBaseModifiers();

  for (const id of inventory.affixes) {
    const affix = AFFIXES.find((a) => a.id === id);
    if (!affix) continue;
    mods.damageMult *= affix.effects.damageMult ?? 1;
    mods.shieldMult *= affix.effects.shieldMult ?? 1;
    mods.speedMult *= affix.effects.speedMult ?? 1;
    mods.salvageMult *= affix.effects.salvageMult ?? 1;
    mods.cooldownMult *= affix.effects.cooldownMult ?? 1;
  }

  for (const id of inventory.components) {
    const component = COMPONENTS.find((c) => c.id === id);
    if (!component) continue;
    mods.hullBonus += component.effects.hullBonus ?? 0;
    mods.shieldBonus += component.effects.shieldBonus ?? 0;
    mods.boostBonus += component.effects.boostBonus ?? 0;
    mods.extractBonus += component.effects.extractBonus ?? 0;
  }

  for (const nodeId of takenNodes) {
    applySkillEffect(mods, nodeId);
  }

  applyPathModifiers(takenNodes, mods);
  return mods;
}
